/**
 * Share tidy: trims wording a sharing app added around a link, never what a person wrote.
 *
 * Rules are a plain table so they are easy to read and tweak. A line is removed only when a
 * rule matches the whole line, and the share must still hold a real link afterwards. Anything
 * unrecognised stays. The caller keeps the untouched text so the trim can be restored.
 */

export interface ShareTidyResult {
  /** The text to build the note from. Equals the input when nothing was trimmed. */
  body: string
  /** The untouched share text, only when something was trimmed. */
  original: string | null
  /** What was removed, for tests and the restore strip. */
  trimmed: Array<{ text: string; rule: string }>
}

interface PackagingRule {
  id: string
  /** Tested against one whole line with its links removed. */
  pattern: RegExp
  /** The wording points at its own link (an app promo), so the link goes too. */
  dropsLinks?: boolean
}

const APPS = '(?:youtube|spotify|tiktok|instagram|reddit|netflix|medium|pinterest|amazon|facebook|x|twitter|airbnb|etsy|ebay|imdb|substack|linkedin)'
const CONTENT_NOUN = '(?:video|post|article|item|product|song|track|playlist|album|podcast|episode|listing|home|recipe|reel|story|page|profile|thread)'

export const PACKAGING_RULES: readonly PackagingRule[] = [
  { id: 'check-out-content', pattern: new RegExp(`^check out (?:this|the|my|[\\p{L}.'’ -]{1,40}'s|[\\p{L}.'’ -]{1,40}’s) ${CONTENT_NOUN}\\b.{0,100}$`, 'iu') },
  { id: 'check-it-out-on-app', pattern: new RegExp(`^check (?:this|it) out (?:on|at|in) ${APPS}\\b.{0,40}$`, 'i') },
  { id: 'verb-on-app', pattern: new RegExp(`^(?:watch|listen to|read|see|view) .{1,120} (?:on|in|at|via) ${APPS}\\b[:!.]*$`, 'i') },
  { id: 'found-on-app', pattern: new RegExp(`^i (?:found|saw|came across|thought of you) .{0,80} (?:on|in) ${APPS}\\b[:!.]*$`, 'i') },
  { id: 'sent-from', pattern: /^(?:sent|shared) (?:from|via|with|using) .{1,60}$/i },
  { id: 'via', pattern: /^via .{1,40}$/i },
  { id: 'get-the-app', pattern: /^(?:get|download|install|open) (?:the |our |this )?.{0,40}\bapp\b.{0,40}$/i, dropsLinks: true },
  { id: 'title-read-in-app', pattern: /^[“"‘'«].{2,200}[”"’'»]\s*[–—-]\s*(?:read|watch|listen to|see|view)(?: it)? (?:in|on|with) (?:the )?.{1,40}$/i },
]

const PROMO_HOSTS = [
  'apps.apple.com',
  'itunes.apple.com',
  'onelink.me',
  'app.link',
  'adj.st',
  'page.link',
]

const URL_PATTERN = /https?:\/\/[^\s<>()[\]{}"']+/gi

function hostOf(url: string): string | null {
  try { return new URL(url).hostname.toLowerCase().replace(/^www\./, '') } catch { return null }
}

function isPromoUrl(url: string): boolean {
  const host = hostOf(url)
  if (!host) return false
  if (PROMO_HOSTS.some((promo) => host === promo || host.endsWith(`.${promo}`))) return true
  try {
    const { pathname } = new URL(url)
    return host === 'play.google.com' && pathname.startsWith('/store/apps')
  } catch { return false }
}

function normalize(value: string): string {
  return value.toLocaleLowerCase().replace(/[\p{P}\p{S}]+/gu, ' ').replace(/\s+/g, ' ').trim()
}

function withoutUrls(line: string): string {
  return line.replace(URL_PATTERN, ' ').replace(/\s+/g, ' ').trim()
}

export function tidyShareText(raw: string, sourceTitle?: string): ShareTidyResult {
  const unchanged: ShareTidyResult = { body: raw, original: null, trimmed: [] }
  const urls = raw.match(URL_PATTERN) ?? []
  const realUrls = urls.filter((url) => !isPromoUrl(url))
  if (!realUrls.length) return unchanged

  const title = sourceTitle?.trim()
  const titleKey = title ? normalize(title) : ''
  const trimmed: ShareTidyResult['trimmed'] = []
  const kept: string[] = []

  for (const line of raw.split(/\r?\n/)) {
    const text = line.trim()
    if (!text) { kept.push(''); continue }
    const lineUrls = text.match(URL_PATTERN) ?? []
    const prose = withoutUrls(text).replace(/[\s:–—-]+$/, '').trim()

    if (lineUrls.length && lineUrls.every(isPromoUrl)) {
      // A promo link, with or without its lead-in ("Get the app: …"), goes entirely.
      trimmed.push({ text, rule: 'promo-link' })
      continue
    }
    if (prose) {
      const matched = titleKey && lineUrls.length === 0 && normalize(prose) === titleKey
        ? { id: 'title-repeat', dropsLinks: false }
        : PACKAGING_RULES.find((candidate) => candidate.pattern.test(prose))
      if (matched) {
        trimmed.push({ text: text, rule: matched.id })
        // Keep any real link that shared the line with the wording.
        const links = matched.dropsLinks ? [] : lineUrls.filter((url) => !isPromoUrl(url))
        if (links.length) kept.push(links.join(' '))
        continue
      }
    }
    kept.push(text)
  }

  if (!trimmed.length) return unchanged
  const body = kept.join('\n').replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '')
  // Safety: never leave a share without its link or with nothing at all.
  if (!body || !(body.match(URL_PATTERN) ?? []).some((url) => !isPromoUrl(url))) return unchanged
  return { body, original: raw, trimmed }
}
